import moment from 'moment';
import i18n from 'i18n-js';

import { localTime } from '@helpers';

export const getValuesLine = ({ start: startStr, data, amount, period }) => {
  const dataStorage = {};
  const isHour = period == 'hour';
  const format = isHour ? 'MM-DD HH:00' : 'MM-DD';

  const dataLineActive = [];
  const dataLineProductive = [];

  if (isHour) {
    dataLineActive.push(0);
    dataLineProductive.push(0);
  }

  data?.forEach((item) => {
    const { totalActiveSecs, totalProductiveSecs, dateTime, date } = item;
    // Backend returns datetime in factory timezone (no indicator), parse as-is without forcing UTC
    const time = localTime(dateTime || date);
    let day = time.format(format);
    const active = totalActiveSecs;
    const productive = totalProductiveSecs;

    dataStorage[day] = {
      active,
      productive,
    };
  });
  let activeAcc = 0;
  let productiveAcc = 0;
  let startCounting = moment(startStr);
  for (let i = 1; i <= amount; i++) {
    const value = dataStorage[startCounting.format(format)];
    activeAcc += value?.active || 0;
    productiveAcc += value?.productive || 0;
    dataLineActive.push(activeAcc);
    dataLineProductive.push(productiveAcc);
    startCounting.add(1, period);
  }

  return {
    totalActive: activeAcc,
    totalProductive: productiveAcc,
    dataLineActive,
    dataLineProductive,
  };
};

export const getValuesBar = ({ start: startStr, data, amount, period }) => {
  const dataStorage = {};
  const isHour = period == 'hour';
  const format = isHour ? 'MM-DD HH:00' : 'MM-DD';
  const dataBar = [];

  const baseLineObject = {
    active: 0,
    productive: 0,
    setup: 0,
    change: 0,
  };

  let totalActive = 0;
  let totalChange = 0;
  let totalSetup = 0;
  let totalProductive = 0;

  data?.forEach((item) => {
    const { totalActiveSecs, totalChangeSecs, totalProductiveSecs, totalSetupSecs, dateTime, date } = item;
    // Backend returns datetime in factory timezone (no indicator), parse as-is without forcing UTC
    const time = localTime(dateTime || date);
    let day = time.format(format);
    const active = totalActiveSecs;
    const change = totalChangeSecs;
    const setup = totalSetupSecs;
    const productive = totalProductiveSecs;

    dataStorage[day] = {
      ...item,
      active,
      productive,
      setup,
      change,
    };
  });

  let startCounting = moment(startStr);
  for (let i = 1; i <= amount; i++) {
    const value = dataStorage[startCounting.format(format)] || baseLineObject;

    let storage = {
      productive: 0,
      active: 0,
      change: 0,
      setup: 0,
    };

    storage.productive += value.productive;
    storage.active += value.active;
    storage.change += value.change;
    storage.setup += value.setup;
    storage.date = `${startCounting.format('DD')} ${i18n.t(startCounting.format('MMM'))} ${startCounting.format('YYYY')}`;
    storage.dateISO = startCounting.format();

    totalActive += value.active;
    totalChange += value.change;
    totalSetup += value.setup;
    totalProductive += value.productive;

    dataBar.push({
      ...storage,
    });

    startCounting.add(1, period);
  }

  return {
    dataBar,
    totalActive,
    totalProductive,
    totalChange,
    totalSetup,
  };
};

const formatNumber = (n) => (n < 10 ? `0${n}` : n);

export const getBarLegend = (selectedTime, start) => {
  switch (selectedTime) {
    case 'day': {
      const legend = [];
      Array.from({ length: 24 }, (_, el) => {
        legend.push(`${formatNumber(el)}`);
      });
      return { legend, legendRules: ['00', '06', '12', '18'] };
    }
    case 'week': {
      const legend = [i18n.t('Mon'), i18n.t('Tue'), i18n.t('Wed'), i18n.t('Thu'), i18n.t('Fri'), i18n.t('Sat'), i18n.t('Sun')];
      return { legend, legendRules: legend };
    }
    case 'month': {
      const startMonth = (start ? moment(start) : localTime()).startOf('month');
      const legend = Array.from({ length: startMonth.daysInMonth() }, (_, i) => i + 1);
      return { legend, legendRules: [7, 14, 21, 28] };
    }
    case 'year': {
      const startYear = moment(start).startOf('year');
      const legend = [];

      Array.from({ length: 12 }, (_, el) => {
        legend.push(startYear.format('MMM').charAt(0));
        startYear.add(1, 'month');
      });
      return { legend, legendRules: legend };
    }
    default:
      return { legend: [], legendRules: [] };
  }
};

export const getLineLegend = ({ selectedTime, start, end, showFullInfo }) => {
  const { legendItems, colorDots, currentLegendInit, colorWhiteDots } = (() => {
    switch (selectedTime) {
      case 'day': {
        const colorWhiteDots = [];
        const colorDots = ['00:00', '06:00', '12:00', '18:00', '23:59'];

        const legendItems = Array.from({ length: 24 }, (_, el) => {
          const d = `${formatNumber(el)}:00`;
          if (!colorDots.includes(d)) {
            colorWhiteDots.push(d);
          }
          return d;
        });
        legendItems.push(`23:59`);
        return {
          legendItems,
          currentLegendInit: localTime().format('HH:00'),
          colorDots,
          colorWhiteDots,
        };
      }
      case 'week': {
        const legendItems = [];
        const startWeek = moment(start);

        const colorDots = [];
        const colorWhiteDots = [];
        const colorWhiteDotsEl = [6, 12, 18];
        Array.from({ length: 7 }, (_, index) => {
          const i = startWeek.format('ddd');
          startWeek.add(1, 'day');
          Array.from({ length: 24 }, (_, el) => {
            const d = `${i} ${formatNumber(el)}:00`;
            if (el === 0) {
              colorDots.push(d);
            }
            if (colorWhiteDotsEl.includes(el)) {
              colorWhiteDots.push(d);
            }
            legendItems.push(d);
          });
          if (index === 6) {
            const d = `${i} 23:59`;
            colorDots.push(d);
            legendItems.push(d);
          }
        });
        return {
          colorDots: colorDots,
          colorWhiteDots: colorWhiteDots,
          currentLegendInit: `${localTime().format('ddd')} ${localTime().format('HH:00')}`,
          legendItems,
        };
      }
      case 'month': {
        const legendItems = [];
        const amountStart = moment.utc(start).daysInMonth();
        const amountEnd = moment.utc(end).daysInMonth();

        const date = amountStart > amountEnd ? start : end;
        const startMonth = moment.utc(date).startOf('month');
        const amount = moment.utc(date).daysInMonth();

        for (let i = 1; i <= amount; i++) {
          legendItems.push(`${i}`);
          startMonth.add(1, 'day');
        }

        const colorWhiteDots = [];
        const colorDots = [legendItems[0], legendItems[legendItems.length - 1]];
        const colorDotsEl = [6, 11, 16, 21, 26];
        const skipItems = [1, 31];

        Array.from({ length: 31 }, (_, i) => {
          const el = i + 1;
          if (skipItems.includes(el)) {
            return;
          }

          if (colorDotsEl.includes(el)) {
            colorDots.push(`${el}`);
          } else {
            colorWhiteDots.push(`${el}`);
          }
        });

        return {
          currentLegendInit: `${Number(localTime().format('DD'))}`,
          legendItems,
          colorDots,
          colorWhiteDots,
        };
      }
      case 'year': {
        const startYear = moment(start).startOf('year');
        const legendItems = [];
        const colorDots = [];
        const colorWhiteDots = [];
        const yearAmount = moment(end).endOf('year').dayOfYear();
        const current = `${localTime().format('ddd')} ${localTime().format('DD')} ${localTime().format('MMM')}`;

        const monthHighlighted = ['3', '6', '9'];
        for (let i = 1; i < yearAmount; i++) {
          if (localTime().format('DD-MM') == startYear.format('DD-MM')) {
            legendItems.push(current);
            startYear.add(1, 'day');
            continue;
          }

          if (i === 1 || i === yearAmount - 1) {
            legendItems.push(`${startYear.format('MMM')}`);
            colorDots.push(`${startYear.format('MMM')}`);
          } else {
            const current = `${startYear.format('ddd')} ${startYear.format('MMM')} ${Number(startYear.format('DD'))}`;
            if (Number(startYear.format('DD')) === 1) {
              if (monthHighlighted.includes(startYear.format('M'))) {
                colorDots.push(current);
              } else {
                colorWhiteDots.push(current);
              }
            }
            legendItems.push(current);
          }
          startYear.add(1, 'day');
        }

        return {
          colorDots,
          legendItems,
          currentLegendInit: current,
          colorWhiteDots,
        };
      }
      default:
        return {
          legendItems: [],
          colorDots: [],
          currentLegendInit: '',
        };
    }
  })();

  const currentLegend = showFullInfo ? legendItems[legendItems.length - 1] : currentLegendInit;

  colorDots.push(currentLegend);

  const bigLegendStorage = {
    day: ['00:00', currentLegend, '23:59'],
    week: [legendItems[0], currentLegend, legendItems[legendItems.length - 1]],
    month: [legendItems[0], currentLegend, legendItems[legendItems.length - 1]],
    year: [legendItems[0], currentLegend, legendItems[legendItems.length - 1]],
  };

  const legendOffsetStorage = {
    day: 4,
    week: 48,
    month: 5,
    year: 60,
  };
  const legendOffset = legendOffsetStorage[selectedTime];

  const bigLegend = bigLegendStorage[selectedTime];

  const displayIndex = showFullInfo ? legendItems.length - 1 : legendItems.indexOf(currentLegend);

  return { legendOffset, bigLegend, colorDots, displayIndex, legendItems, currentLegend, colorWhiteDots };
};
